// Translation dictionary for Holy Words — Phase 1.
// Covers the top ~50 highest-visibility strings: header nav, footer,
// daily devotional card, 3 daily cards, settings labels, video player
// buttons. Adding a new string requires translating it into all 10
// supported languages so the UI stays consistent.
//
// Why hand-rolled translations and not an LLM call at runtime? Two
// reasons:
//   1. UI labels are tiny and rarely change — paying the LLM latency
//      on every Header re-render is silly.
//   2. Reviewers can audit translations in a single PR diff instead
//      of inspecting model output per request.
//
// Languages match `lib/constants.js > LANGUAGES`. en is the English
// source of truth that everything else translates from.
const translations = {
  en: {
    // Header / global navigation
    "nav.bible": "Bible",
    "nav.rooms": "Rooms",
    "nav.prayers": "Prayers",
    "nav.sermons": "Sermons",
    "nav.movies": "Movies",
    "nav.kids": "Kids",
    "nav.churches": "Churches",
    "nav.more": "More",
    "nav.signIn": "Sign In",
    "nav.signOut": "Out",
    "nav.goLive": "Go Live",
    "nav.home": "Home",
    "nav.settings": "Settings",
    // Footer
    "footer.brand": "Holy Words · An open Bible companion",
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.settings": "Settings",
    // Daily Devotional card
    "today.devotionalLabel": "Today's devotional",
    "today.devotionalHeading": "One verse. One reflection. Right where you are.",
    "today.readAloud": "Read aloud",
    "today.pause": "Pause",
    "today.playBriefing": "Play today's briefing",
    // Three daily cards
    "today.onThisDay": "On this day in Black history",
    "today.somethingGood": "Something good",
    "today.someoneToKnow": "Someone you should know",
    "today.previousDays": "Previous days",
    "today.readMore": "Read more",
    // Tonight / handpicked
    "today.handpickedTitle": "Hand-picked for tonight",
    "today.seeAll": "See all",
    // Common actions
    "action.play": "Play",
    "action.pause": "Pause",
    "action.stop": "Stop",
    "action.share": "Share",
    "action.save": "Save",
    "action.cancel": "Cancel",
    "action.close": "Close",
    "action.gotIt": "Got it",
    "action.upload": "Upload",
    "action.post": "Post",
    "action.loading": "Loading…",
    "action.tryAgain": "Try again",
    "action.scrollTop": "Scroll to top",
    "action.scrollBottom": "Scroll to bottom",
    // Verse of the day
    "verse.label": "Verse of the day",
    "verse.openChapter": "Open chapter",
    // Settings labels (top-level)
    "settings.title": "Settings",
    "settings.account": "Account",
    "settings.language": "Language",
    "settings.notifications": "Notifications",
    "settings.theme": "Theme",
    "settings.signOut": "Sign out",
    "settings.premium": "Premium",
    // Player / video
    "player.nowPlaying": "Now playing",
    "player.upNext": "Up next",
    "player.skip": "Skip",
    // Chip rail (alphabetized A→Z, used in CategoryChipRail.jsx)
    "chip.home": "Home",
    "chip.forYou": "For You",
    "chip.live": "Live",
    "chip.trending": "Trending",
    "chip.animatedBible": "Animated Bible",
    "chip.apocrypha": "Apocrypha",
    "chip.bible": "Bible",
    "chip.biblePrecepts": "Bible Precepts",
    "chip.blackAmericanFilms": "Black American Films",
    "chip.bookmarks": "Bookmarks",
    "chip.christianRap": "Faith-Based Rap",
    "chip.choir": "Choir",
    "chip.churches": "Churches",
    "chip.comedy": "Comedy",
    "chip.communityShorts": "Community Shorts",
    "chip.concordance": "Concordance",
    "chip.congregationLive": "Congregation Live",
    "chip.creatorStudio": "Creator Studio",
    "chip.dailyDevotional": "Daily Devotional",
    "chip.deviceCheck": "Device Check",
    "chip.dictionary": "Dictionary",
    "chip.discoverShorts": "Discover Shorts",
    "chip.faithNews": "Faith News",
    "chip.foundationalComedy": "Foundational Comedy",
    "chip.friends": "Friends",
    "chip.gospelMusic": "Gospel Music",
    "chip.history": "History",
    "chip.kidsCartoons": "Kids & Cartoons",
    "chip.memorial": "Memorial",
    "chip.messages": "Messages",
    "chip.movies": "Movies",
    "chip.musicVideos": "Music Videos",
    "chip.myLineup": "My Lineup",
    "chip.myMix": "My Mix",
    "chip.myNotes": "My Notes",
    "chip.myPrayers": "My Prayers",
    "chip.myProfile": "My Profile",
    "chip.pictureBible": "Picture Bible",
    "chip.prayerCircles": "Prayer Circles",
    "chip.prayers": "Prayers",
    "chip.playlists": "Playlists",
    "chip.puzzles": "Puzzles",
    "chip.rooms": "Rooms",
    "chip.search": "Search",
    "chip.sermons": "Sermons",
    "chip.settings": "Settings",
    "chip.studyGroup": "Study Group",
    "chip.subscriptions": "Subscriptions",
    "chip.sundayDinner": "Sunday Dinner",
    "chip.sundaySchool": "Sunday School",
    "chip.supportUs": "Support Us",
    "chip.wallOfFaith": "Wall of Faith",
    "chip.wedLessons": "Wed Lessons",
    // Page headers (Phase 3)
    "page.sermons.kicker": "From the pulpit",
    "page.sermons.title": "Sunday Sermons",
    "page.sermons.subtitle": "A growing library of AI-prepared sermons — every one a fresh passage. Listen in studio-quality HD voice right here on Holy Words.",
    "page.prayers.kicker": "The Upper Room",
    "page.prayers.title": "Prayer |Wall",
    "page.prayers.subtitle": "\"Bear ye one another's burdens, and so fulfil the law of Christ.\" — Galatians 6:2",
    "page.rooms.kicker": "Fellowship",
    "page.rooms.title": "Study |Rooms",
    "page.rooms.subtitle": "Spin up a room for a Bible book, a topic, a small group, or a season. Anyone can join · owners moderate.",
    "page.movies.kicker": "Faith on screen",
    "page.movies.title": "Movies",
    "page.bible.kicker": "The Holy Scriptures",
    "page.bible.title": "Browse the Bible",
    "page.bible.subtitle": "Pick a book and a chapter. Tap the play button on the reader to hear it aloud in your language.",
    "page.bible.tabAll": "All 66",
    "page.bible.tabOld": "Old Testament",
    "page.bible.tabNew": "New Testament",
    "page.bible.filterPlaceholder": "Filter books...",
    // Phase 4 — more pages + auth forms
    "page.kids.kicker": "For the little ones",
    "page.kids.title": "Kids & Cartoons",
    "page.churches.kicker": "Find a home church",
    "page.churches.title": "Church Finder",
    "page.churches.subtitle": "Tap one button to find every Christian church near you — across all denominations. Powered by open community map data.",
    "page.gospel.kicker": "Soul music",
    "page.gospel.title": "Gospel Music",
    "page.discover.kicker": "Trending this week",
    "page.discover.title": "Discover",
    "page.discover.subtitle": "The sermon clips, song lyrics, and movie lines our community is sharing the most right now — auto-curated from the share-this-exact-moment button. Tap any card to jump straight to that second.",
    "auth.welcomeBack": "Welcome back",
    "auth.signInSubtitle": "Sign in to access your saved verses.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.forgotPassword": "Forgot password?",
    "auth.signingIn": "Signing in…",
    "auth.or": "or",
    "auth.continueWithGoogle": "Continue with Google",
    "discover.empty.title": "No moments shared yet this week",
    "discover.empty.body": "While you're watching or listening to anything — a sermon, short, live, song or video — tap the 🔥 Pin button in the player to save the exact moment you love. It lands right here, and anyone who taps it jumps straight to that spot. The moments pinned most this week light the board up. Yours could be next.",
    "discover.empty.cta": "Start with sermons",
    "discover.loading": "Loading the leaderboard…",
    "discover.error": "Couldn't load the leaderboard right now. Try again in a moment.",
    "voice.hintKicker": "Try voice control",
    "voice.hintBody": "Say \"play Casting Crowns\"",
    "voice.hintHelp": "Tap the mic to start →",
  },

  es: {
    "nav.bible": "Biblia",
    "nav.rooms": "Salas",
    "nav.prayers": "Oraciones",
    "nav.sermons": "Sermones",
    "nav.movies": "Películas",
    "nav.kids": "Niños",
    "nav.churches": "Iglesias",
    "nav.more": "Más",
    "nav.signIn": "Iniciar sesión",
    "nav.signOut": "Salir",
    "nav.goLive": "En vivo",
    "nav.home": "Inicio",
    "nav.settings": "Ajustes",
    "footer.brand": "Holy Words · Tu compañero bíblico abierto",
    "footer.privacy": "Privacidad",
    "footer.terms": "Términos",
    "footer.settings": "Ajustes",
    "today.devotionalLabel": "Devocional de hoy",
    "today.devotionalHeading": "Un versículo. Una reflexión. Justo donde estás.",
    "today.readAloud": "Leer en voz alta",
    "today.pause": "Pausar",
    "today.playBriefing": "Reproducir resumen de hoy",
    "today.onThisDay": "En este día en la historia negra",
    "today.somethingGood": "Algo bueno",
    "today.someoneToKnow": "Alguien que debes conocer",
    "today.previousDays": "Días anteriores",
    "today.readMore": "Leer más",
    "today.handpickedTitle": "Seleccionados para esta noche",
    "today.seeAll": "Ver todo",
    "action.play": "Reproducir",
    "action.pause": "Pausar",
    "action.stop": "Detener",
    "action.share": "Compartir",
    "action.save": "Guardar",
    "action.cancel": "Cancelar",
    "action.close": "Cerrar",
    "action.gotIt": "Entendido",
    "action.upload": "Subir",
    "action.post": "Publicar",
    "action.loading": "Cargando…",
    "action.tryAgain": "Intentar de nuevo",
    "action.scrollTop": "Ir arriba",
    "action.scrollBottom": "Ir abajo",
    "verse.label": "Versículo del día",
    "verse.openChapter": "Abrir capítulo",
    "settings.title": "Ajustes",
    "settings.account": "Cuenta",
    "settings.language": "Idioma",
    "settings.notifications": "Notificaciones",
    "settings.theme": "Tema",
    "settings.signOut": "Cerrar sesión",
    "settings.premium": "Premium",
    "player.nowPlaying": "Reproduciendo ahora",
    "player.upNext": "A continuación",
    "player.skip": "Saltar",
    "chip.home": "Inicio",
    "chip.trending": "Tendencias",
    "chip.animatedBible": "Biblia animada",
    "chip.apocrypha": "Apócrifos",
    "chip.bible": "Biblia",
    "chip.biblePrecepts": "Preceptos bíblicos",
    "chip.blackAmericanFilms": "Cine afroamericano",
    "chip.bookmarks": "Favoritos",
    "chip.christianRap": "Rap cristiano",
    "chip.choir": "Coro",
    "chip.churches": "Iglesias",
    "chip.comedy": "Comedia",
    "chip.communityShorts": "Cortos comunitarios",
    "chip.concordance": "Concordancia",
    "chip.congregationLive": "Congregación en vivo",
    "chip.creatorStudio": "Estudio del creador",
    "chip.dailyDevotional": "Devocional diario",
    "chip.deviceCheck": "Verificar dispositivo",
    "chip.dictionary": "Diccionario",
    "chip.discoverShorts": "Descubre cortos",
    "chip.faithNews": "Noticias de fe",
    "chip.foundationalComedy": "Comedia fundacional",
    "chip.friends": "Amigos",
    "chip.gospelMusic": "Música gospel",
    "chip.history": "Historia",
    "chip.kidsCartoons": "Niños y dibujos",
    "chip.memorial": "Memorial",
    "chip.messages": "Mensajes",
    "chip.movies": "Películas",
    "chip.musicVideos": "Videos musicales",
    "chip.myLineup": "Mi cartelera",
    "chip.myMix": "Mi mezcla",
    "chip.myNotes": "Mis notas",
    "chip.myPrayers": "Mis oraciones",
    "chip.myProfile": "Mi perfil",
    "chip.pictureBible": "Biblia ilustrada",
    "chip.prayerCircles": "Círculos de oración",
    "chip.prayers": "Oraciones",
    "chip.playlists": "Listas",
    "chip.puzzles": "Rompecabezas",
    "chip.rooms": "Salas",
    "chip.search": "Buscar",
    "chip.sermons": "Sermones",
    "chip.settings": "Ajustes",
    "chip.studyGroup": "Grupo de estudio",
    "chip.subscriptions": "Suscripciones",
    "chip.sundayDinner": "Cena dominical",
    "chip.sundaySchool": "Escuela dominical",
    "chip.supportUs": "Apóyanos",
    "chip.wallOfFaith": "Muro de fe",
    "chip.wedLessons": "Lecciones del miércoles",
    "page.sermons.kicker": "Desde el púlpito",
    "page.sermons.title": "Sermones dominicales",
    "page.sermons.subtitle": "Una biblioteca creciente de sermones preparados por IA — cada uno un pasaje fresco. Escúchalos en voz HD de calidad estudio o suscríbete como podcast.",
    "page.prayers.kicker": "El Aposento Alto",
    "page.prayers.title": "Muro de |Oración",
    "page.prayers.subtitle": "\"Sobrellevad los unos las cargas de los otros, y cumplid así la ley de Cristo.\" — Gálatas 6:2",
    "page.rooms.kicker": "Compañerismo",
    "page.rooms.title": "Salas de |estudio",
    "page.rooms.subtitle": "Crea una sala para un libro bíblico, un tema, un grupo pequeño o una temporada. Cualquiera puede unirse · los dueños moderan.",
    "page.movies.kicker": "Fe en pantalla",
    "page.movies.title": "Películas",
    "page.bible.kicker": "Las Sagradas Escrituras",
    "page.bible.title": "Explora la Biblia",
    "page.bible.subtitle": "Elige un libro y un capítulo. Toca el botón de reproducir para oírlo en tu idioma.",
    "page.bible.tabAll": "Los 66",
    "page.bible.tabOld": "Antiguo Testamento",
    "page.bible.tabNew": "Nuevo Testamento",
    "page.bible.filterPlaceholder": "Filtrar libros...",
    "page.kids.kicker": "Para los pequeños",
    "page.kids.title": "Niños y dibujos",
    "page.churches.kicker": "Encuentra tu iglesia",
    "page.churches.title": "Buscador de iglesias",
    "page.churches.subtitle": "Toca un botón para encontrar todas las iglesias cristianas cerca de ti — de cualquier denominación. Datos de mapas abiertos de la comunidad.",
    "page.gospel.kicker": "Música del alma",
    "page.gospel.title": "Música gospel",
    "page.discover.kicker": "Tendencias de la semana",
    "page.discover.title": "Descubrir",
    "page.discover.subtitle": "Los clips de sermones, letras y frases que nuestra comunidad comparte ahora mismo — curado por el botón compartir-este-momento. Toca cualquier tarjeta para saltar a ese segundo.",
    "auth.welcomeBack": "Bienvenido de nuevo",
    "auth.signInSubtitle": "Inicia sesión para acceder a tus versículos guardados.",
    "auth.email": "Correo",
    "auth.password": "Contraseña",
    "auth.forgotPassword": "¿Olvidaste tu contraseña?",
    "auth.signingIn": "Iniciando sesión…",
    "auth.or": "o",
    "auth.continueWithGoogle": "Continuar con Google",
    "discover.empty.title": "Aún no hay momentos compartidos esta semana",
    "discover.empty.body": "Mientras ves o escuchas algo —un sermón, un short, un directo, una canción o un video— toca el botón 🔥 Pin en el reproductor para guardar el momento exacto que te gusta. Aparecerá aquí, y quien lo toque saltará justo a ese punto. Los momentos más fijados de la semana iluminan el tablero. El tuyo podría ser el próximo.",
    "discover.empty.cta": "Empieza con los sermones",
    "discover.loading": "Cargando el ranking…",
    "discover.error": "No pudimos cargar el ranking ahora. Inténtalo en un momento.",
    "voice.hintKicker": "Prueba el control por voz",
    "voice.hintBody": "Di \"play Casting Crowns\"",
    "voice.hintHelp": "Toca el micro para empezar →",
  },

  fr: {
    "nav.bible": "Bible",
    "nav.rooms": "Salons",
    "nav.prayers": "Prières",
    "nav.sermons": "Sermons",
    "nav.movies": "Films",
    "nav.kids": "Enfants",
    "nav.churches": "Églises",
    "nav.more": "Plus",
    "nav.signIn": "Connexion",
    "nav.signOut": "Sortir",
    "nav.goLive": "En direct",
    "nav.home": "Accueil",
    "nav.settings": "Paramètres",
    "footer.brand": "Holy Words · Votre compagnon biblique ouvert",
    "footer.privacy": "Confidentialité",
    "footer.terms": "Conditions",
    "footer.settings": "Paramètres",
    "today.devotionalLabel": "Dévotion du jour",
    "today.devotionalHeading": "Un verset. Une réflexion. Là où vous êtes.",
    "today.readAloud": "Lire à voix haute",
    "today.pause": "Pause",
    "today.playBriefing": "Lire le résumé du jour",
    "today.onThisDay": "Ce jour dans l'histoire noire",
    "today.somethingGood": "Quelque chose de bon",
    "today.someoneToKnow": "Quelqu'un à connaître",
    "today.previousDays": "Jours précédents",
    "today.readMore": "Lire plus",
    "today.handpickedTitle": "Sélection de ce soir",
    "today.seeAll": "Voir tout",
    "action.play": "Lecture",
    "action.pause": "Pause",
    "action.stop": "Arrêter",
    "action.share": "Partager",
    "action.save": "Enregistrer",
    "action.cancel": "Annuler",
    "action.close": "Fermer",
    "action.gotIt": "Compris",
    "action.upload": "Téléverser",
    "action.post": "Publier",
    "action.loading": "Chargement…",
    "action.tryAgain": "Réessayer",
    "action.scrollTop": "Aller en haut",
    "action.scrollBottom": "Aller en bas",
    "verse.label": "Verset du jour",
    "verse.openChapter": "Ouvrir le chapitre",
    "settings.title": "Paramètres",
    "settings.account": "Compte",
    "settings.language": "Langue",
    "settings.notifications": "Notifications",
    "settings.theme": "Thème",
    "settings.signOut": "Se déconnecter",
    "settings.premium": "Premium",
    "player.nowPlaying": "En cours",
    "player.upNext": "À suivre",
    "player.skip": "Passer",
    "chip.home": "Accueil",
    "chip.trending": "Tendances",
    "chip.animatedBible": "Bible animée",
    "chip.apocrypha": "Apocryphes",
    "chip.bible": "Bible",
    "chip.biblePrecepts": "Préceptes bibliques",
    "chip.blackAmericanFilms": "Films afro-américains",
    "chip.bookmarks": "Favoris",
    "chip.christianRap": "Rap chrétien",
    "chip.choir": "Chorale",
    "chip.churches": "Églises",
    "chip.comedy": "Comédie",
    "chip.communityShorts": "Courts communautaires",
    "chip.concordance": "Concordance",
    "chip.congregationLive": "Assemblée en direct",
    "chip.creatorStudio": "Studio créateur",
    "chip.dailyDevotional": "Dévotion quotidienne",
    "chip.deviceCheck": "Vérifier l'appareil",
    "chip.dictionary": "Dictionnaire",
    "chip.discoverShorts": "Découvrir les courts",
    "chip.faithNews": "Actu de foi",
    "chip.foundationalComedy": "Comédie fondatrice",
    "chip.friends": "Amis",
    "chip.gospelMusic": "Musique gospel",
    "chip.history": "Histoire",
    "chip.kidsCartoons": "Enfants & dessins",
    "chip.memorial": "Mémorial",
    "chip.messages": "Messages",
    "chip.movies": "Films",
    "chip.musicVideos": "Clips musicaux",
    "chip.myLineup": "Ma programmation",
    "chip.myMix": "Mon mix",
    "chip.myNotes": "Mes notes",
    "chip.myPrayers": "Mes prières",
    "chip.myProfile": "Mon profil",
    "chip.pictureBible": "Bible illustrée",
    "chip.prayerCircles": "Cercles de prière",
    "chip.prayers": "Prières",
    "chip.playlists": "Playlists",
    "chip.puzzles": "Puzzles",
    "chip.rooms": "Salons",
    "chip.search": "Recherche",
    "chip.sermons": "Sermons",
    "chip.settings": "Paramètres",
    "chip.studyGroup": "Groupe d'étude",
    "chip.subscriptions": "Abonnements",
    "chip.sundayDinner": "Dîner du dimanche",
    "chip.sundaySchool": "École du dimanche",
    "chip.supportUs": "Soutenez-nous",
    "chip.wallOfFaith": "Mur de la foi",
    "chip.wedLessons": "Leçons du mercredi",
    "page.sermons.kicker": "Depuis la chaire",
    "page.sermons.title": "Sermons du dimanche",
    "page.sermons.subtitle": "Une bibliothèque grandissante de sermons préparés par IA — chacun un passage frais. Écoutez en voix HD studio ou abonnez-vous au podcast.",
    "page.prayers.kicker": "La chambre haute",
    "page.prayers.title": "Mur de |prière",
    "page.prayers.subtitle": "\"Portez les fardeaux les uns des autres, et vous accomplirez ainsi la loi de Christ.\" — Galates 6:2",
    "page.rooms.kicker": "Communion",
    "page.rooms.title": "Salons |d'étude",
    "page.rooms.subtitle": "Créez un salon pour un livre biblique, un sujet, un petit groupe ou une saison. Tous peuvent rejoindre · les propriétaires modèrent.",
    "page.movies.kicker": "Foi à l'écran",
    "page.movies.title": "Films",
    "page.bible.kicker": "Les Saintes Écritures",
    "page.bible.title": "Parcourir la Bible",
    "page.bible.subtitle": "Choisissez un livre et un chapitre. Touchez le bouton lecture pour l'entendre dans votre langue.",
    "page.bible.tabAll": "Les 66",
    "page.bible.tabOld": "Ancien Testament",
    "page.bible.tabNew": "Nouveau Testament",
    "page.bible.filterPlaceholder": "Filtrer les livres...",
    "page.kids.kicker": "Pour les petits",
    "page.kids.title": "Enfants & dessins",
    "page.churches.kicker": "Trouvez votre église",
    "page.churches.title": "Annuaire des églises",
    "page.churches.subtitle": "Touchez un bouton pour trouver toutes les églises chrétiennes près de chez vous — toutes dénominations. Données cartographiques ouvertes.",
    "page.gospel.kicker": "Musique de l'âme",
    "page.gospel.title": "Musique gospel",
    "page.discover.kicker": "Tendances de la semaine",
    "page.discover.title": "Découvrir",
    "page.discover.subtitle": "Les extraits de sermons, paroles et répliques que notre communauté partage le plus en ce moment — curaté par le bouton partager-ce-moment. Touchez une carte pour aller à cette seconde.",
    "auth.welcomeBack": "Bon retour",
    "auth.signInSubtitle": "Connectez-vous pour accéder à vos versets enregistrés.",
    "auth.email": "Email",
    "auth.password": "Mot de passe",
    "auth.forgotPassword": "Mot de passe oublié ?",
    "auth.signingIn": "Connexion…",
    "auth.or": "ou",
    "auth.continueWithGoogle": "Continuer avec Google",
    "discover.empty.title": "Aucun moment partagé cette semaine",
    "discover.empty.body": "Pendant que vous regardez ou écoutez quelque chose — un sermon, un short, un direct, un chant ou une vidéo — appuyez sur le bouton 🔥 Pin du lecteur pour enregistrer le moment exact que vous aimez. Il apparaîtra ici, et quiconque le touche sautera directement à cet instant. Les moments les plus épinglés de la semaine illuminent le classement. Le vôtre pourrait être le prochain.",
    "discover.empty.cta": "Commencez par les sermons",
    "discover.loading": "Chargement du classement…",
    "discover.error": "Impossible de charger le classement pour l'instant. Réessayez dans un instant.",
    "voice.hintKicker": "Essayez la commande vocale",
    "voice.hintBody": "Dites \"play Casting Crowns\"",
    "voice.hintHelp": "Appuyez sur le micro pour commencer →",
  },

  de: {
    "nav.bible": "Bibel",
    "nav.rooms": "Räume",
    "nav.prayers": "Gebete",
    "nav.sermons": "Predigten",
    "nav.movies": "Filme",
    "nav.kids": "Kinder",
    "nav.churches": "Kirchen",
    "nav.more": "Mehr",
    "nav.signIn": "Anmelden",
    "nav.signOut": "Abmelden",
    "nav.goLive": "Live gehen",
    "nav.home": "Start",
    "nav.settings": "Einstellungen",
    "footer.brand": "Holy Words · Dein offener Bibelbegleiter",
    "footer.privacy": "Datenschutz",
    "footer.terms": "Bedingungen",
    "footer.settings": "Einstellungen",
    "today.devotionalLabel": "Andacht des Tages",
    "today.devotionalHeading": "Ein Vers. Eine Reflexion. Genau dort, wo du bist.",
    "today.readAloud": "Vorlesen",
    "today.pause": "Pause",
    "today.playBriefing": "Tages-Briefing abspielen",
    "today.onThisDay": "An diesem Tag in der Schwarzen Geschichte",
    "today.somethingGood": "Etwas Gutes",
    "today.someoneToKnow": "Jemand, den du kennen solltest",
    "today.previousDays": "Frühere Tage",
    "today.readMore": "Mehr lesen",
    "today.handpickedTitle": "Heute Abend handverlesen",
    "today.seeAll": "Alle anzeigen",
    "action.play": "Abspielen",
    "action.pause": "Pause",
    "action.stop": "Stopp",
    "action.share": "Teilen",
    "action.save": "Speichern",
    "action.cancel": "Abbrechen",
    "action.close": "Schließen",
    "action.gotIt": "Verstanden",
    "action.upload": "Hochladen",
    "action.post": "Posten",
    "action.loading": "Lädt…",
    "action.tryAgain": "Erneut versuchen",
    "action.scrollTop": "Nach oben",
    "action.scrollBottom": "Nach unten",
    "verse.label": "Vers des Tages",
    "verse.openChapter": "Kapitel öffnen",
    "settings.title": "Einstellungen",
    "settings.account": "Konto",
    "settings.language": "Sprache",
    "settings.notifications": "Benachrichtigungen",
    "settings.theme": "Design",
    "settings.signOut": "Abmelden",
    "settings.premium": "Premium",
    "player.nowPlaying": "Wird abgespielt",
    "player.upNext": "Als Nächstes",
    "player.skip": "Überspringen",
    "chip.home": "Start",
    "chip.trending": "Trends",
    "chip.animatedBible": "Animierte Bibel",
    "chip.apocrypha": "Apokryphen",
    "chip.bible": "Bibel",
    "chip.biblePrecepts": "Bibel-Lehren",
    "chip.blackAmericanFilms": "Afroamerikanische Filme",
    "chip.bookmarks": "Lesezeichen",
    "chip.christianRap": "Christlicher Rap",
    "chip.choir": "Chor",
    "chip.churches": "Kirchen",
    "chip.comedy": "Komödie",
    "chip.communityShorts": "Community-Kurzvideos",
    "chip.concordance": "Konkordanz",
    "chip.congregationLive": "Gemeinde live",
    "chip.creatorStudio": "Creator-Studio",
    "chip.dailyDevotional": "Tägliche Andacht",
    "chip.deviceCheck": "Geräte-Check",
    "chip.dictionary": "Wörterbuch",
    "chip.discoverShorts": "Kurzvideos entdecken",
    "chip.faithNews": "Glaubensnachrichten",
    "chip.foundationalComedy": "Klassische Komödie",
    "chip.friends": "Freunde",
    "chip.gospelMusic": "Gospelmusik",
    "chip.history": "Geschichte",
    "chip.kidsCartoons": "Kinder & Cartoons",
    "chip.memorial": "Gedenken",
    "chip.messages": "Nachrichten",
    "chip.movies": "Filme",
    "chip.musicVideos": "Musikvideos",
    "chip.myLineup": "Meine Aufstellung",
    "chip.myMix": "Mein Mix",
    "chip.myNotes": "Meine Notizen",
    "chip.myPrayers": "Meine Gebete",
    "chip.myProfile": "Mein Profil",
    "chip.pictureBible": "Bilderbibel",
    "chip.prayerCircles": "Gebetskreise",
    "chip.prayers": "Gebete",
    "chip.playlists": "Playlists",
    "chip.puzzles": "Puzzles",
    "chip.rooms": "Räume",
    "chip.search": "Suche",
    "chip.sermons": "Predigten",
    "chip.settings": "Einstellungen",
    "chip.studyGroup": "Studiengruppe",
    "chip.subscriptions": "Abos",
    "chip.sundayDinner": "Sonntagsessen",
    "chip.sundaySchool": "Sonntagsschule",
    "chip.supportUs": "Unterstützen",
    "chip.wallOfFaith": "Wand des Glaubens",
    "chip.wedLessons": "Mittwoch-Lektionen",
    "page.sermons.kicker": "Von der Kanzel",
    "page.sermons.title": "Sonntagspredigten",
    "page.sermons.subtitle": "Eine wachsende Bibliothek von KI-vorbereiteten Predigten. Hören Sie in Studio-HD-Qualität oder abonnieren Sie als Podcast.",
    "page.prayers.kicker": "Der Obersaal",
    "page.prayers.title": "Gebets|wand",
    "page.prayers.subtitle": "\"Tragt einer des anderen Last, so werdet ihr das Gesetz Christi erfüllen.\" — Galater 6:2",
    "page.rooms.kicker": "Gemeinschaft",
    "page.rooms.title": "Studien|räume",
    "page.rooms.subtitle": "Erstellen Sie einen Raum für ein Bibelbuch, ein Thema, eine Kleingruppe oder eine Saison. Alle können beitreten · Eigentümer moderieren.",
    "page.movies.kicker": "Glaube auf der Leinwand",
    "page.movies.title": "Filme",
    "page.bible.kicker": "Die Heilige Schrift",
    "page.bible.title": "Bibel durchsuchen",
    "page.bible.subtitle": "Wählen Sie ein Buch und ein Kapitel. Tippen Sie auf Play, um es in Ihrer Sprache zu hören.",
    "page.bible.tabAll": "Alle 66",
    "page.bible.tabOld": "Altes Testament",
    "page.bible.tabNew": "Neues Testament",
    "page.bible.filterPlaceholder": "Bücher filtern...",
    "page.kids.kicker": "Für die Kleinen",
    "page.kids.title": "Kinder & Cartoons",
    "page.churches.kicker": "Eine Heimatkirche finden",
    "page.churches.title": "Kirchenfinder",
    "page.churches.subtitle": "Tippen Sie auf einen Knopf, um jede christliche Kirche in Ihrer Nähe zu finden — alle Konfessionen. Powered by offene Community-Kartendaten.",
    "page.gospel.kicker": "Soulmusik",
    "page.gospel.title": "Gospelmusik",
    "page.discover.kicker": "Trends der Woche",
    "page.discover.title": "Entdecken",
    "page.discover.subtitle": "Die Predigtausschnitte, Liedtexte und Filmzitate, die unsere Gemeinschaft gerade am meisten teilt. Tippen Sie auf eine Karte, um direkt zu diesem Moment zu springen.",
    "auth.welcomeBack": "Willkommen zurück",
    "auth.signInSubtitle": "Melden Sie sich an, um auf Ihre gespeicherten Verse zuzugreifen.",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.forgotPassword": "Passwort vergessen?",
    "auth.signingIn": "Anmeldung…",
    "auth.or": "oder",
    "auth.continueWithGoogle": "Mit Google fortfahren",
  },

  pt: {
    "nav.bible": "Bíblia",
    "nav.rooms": "Salas",
    "nav.prayers": "Orações",
    "nav.sermons": "Sermões",
    "nav.movies": "Filmes",
    "nav.kids": "Crianças",
    "nav.churches": "Igrejas",
    "nav.more": "Mais",
    "nav.signIn": "Entrar",
    "nav.signOut": "Sair",
    "nav.goLive": "Ao vivo",
    "nav.home": "Início",
    "nav.settings": "Configurações",
    "footer.brand": "Holy Words · Seu companheiro bíblico aberto",
    "footer.privacy": "Privacidade",
    "footer.terms": "Termos",
    "footer.settings": "Configurações",
    "today.devotionalLabel": "Devocional de hoje",
    "today.devotionalHeading": "Um versículo. Uma reflexão. Bem onde você está.",
    "today.readAloud": "Ouvir em voz alta",
    "today.pause": "Pausar",
    "today.playBriefing": "Reproduzir resumo de hoje",
    "today.onThisDay": "Neste dia na história negra",
    "today.somethingGood": "Algo bom",
    "today.someoneToKnow": "Alguém que você deve conhecer",
    "today.previousDays": "Dias anteriores",
    "today.readMore": "Ler mais",
    "today.handpickedTitle": "Selecionados para esta noite",
    "today.seeAll": "Ver tudo",
    "action.play": "Reproduzir",
    "action.pause": "Pausar",
    "action.stop": "Parar",
    "action.share": "Compartilhar",
    "action.save": "Salvar",
    "action.cancel": "Cancelar",
    "action.close": "Fechar",
    "action.gotIt": "Entendi",
    "action.upload": "Enviar",
    "action.post": "Publicar",
    "action.loading": "Carregando…",
    "action.tryAgain": "Tentar novamente",
    "action.scrollTop": "Ir para o topo",
    "action.scrollBottom": "Ir para o fim",
    "verse.label": "Versículo do dia",
    "verse.openChapter": "Abrir capítulo",
    "settings.title": "Configurações",
    "settings.account": "Conta",
    "settings.language": "Idioma",
    "settings.notifications": "Notificações",
    "settings.theme": "Tema",
    "settings.signOut": "Sair",
    "settings.premium": "Premium",
    "player.nowPlaying": "Tocando agora",
    "player.upNext": "A seguir",
    "player.skip": "Pular",
    "chip.home": "Início",
    "chip.trending": "Em alta",
    "chip.animatedBible": "Bíblia animada",
    "chip.apocrypha": "Apócrifos",
    "chip.bible": "Bíblia",
    "chip.biblePrecepts": "Preceitos bíblicos",
    "chip.blackAmericanFilms": "Cinema afro-americano",
    "chip.bookmarks": "Favoritos",
    "chip.christianRap": "Rap cristão",
    "chip.choir": "Coral",
    "chip.churches": "Igrejas",
    "chip.comedy": "Comédia",
    "chip.communityShorts": "Curtas da comunidade",
    "chip.concordance": "Concordância",
    "chip.congregationLive": "Congregação ao vivo",
    "chip.creatorStudio": "Estúdio do criador",
    "chip.dailyDevotional": "Devocional diário",
    "chip.deviceCheck": "Verificar dispositivo",
    "chip.dictionary": "Dicionário",
    "chip.discoverShorts": "Descobrir curtas",
    "chip.faithNews": "Notícias de fé",
    "chip.foundationalComedy": "Comédia fundacional",
    "chip.friends": "Amigos",
    "chip.gospelMusic": "Música gospel",
    "chip.history": "História",
    "chip.kidsCartoons": "Crianças e desenhos",
    "chip.memorial": "Memorial",
    "chip.messages": "Mensagens",
    "chip.movies": "Filmes",
    "chip.musicVideos": "Clipes",
    "chip.myLineup": "Minha programação",
    "chip.myMix": "Minha mistura",
    "chip.myNotes": "Minhas notas",
    "chip.myPrayers": "Minhas orações",
    "chip.myProfile": "Meu perfil",
    "chip.pictureBible": "Bíblia ilustrada",
    "chip.prayerCircles": "Círculos de oração",
    "chip.prayers": "Orações",
    "chip.playlists": "Listas",
    "chip.puzzles": "Rompecabezas",
    "chip.rooms": "Salas",
    "chip.search": "Buscar",
    "chip.sermons": "Sermões",
    "chip.settings": "Configurações",
    "chip.studyGroup": "Grupo de estudo",
    "chip.subscriptions": "Assinaturas",
    "chip.sundayDinner": "Jantar de domingo",
    "chip.sundaySchool": "Escola dominical",
    "chip.supportUs": "Apoie-nos",
    "chip.wallOfFaith": "Muro da fé",
    "chip.wedLessons": "Lições de quarta",
    "page.sermons.kicker": "Do púlpito",
    "page.sermons.title": "Sermões dominicais",
    "page.sermons.subtitle": "Uma biblioteca crescente de sermões preparados por IA. Ouça em voz HD ou assine como podcast.",
    "page.prayers.kicker": "O Cenáculo",
    "page.prayers.title": "Muro de |oração",
    "page.prayers.subtitle": "\"Levai as cargas uns dos outros e cumprireis assim a lei de Cristo.\" — Gálatas 6:2",
    "page.rooms.kicker": "Comunhão",
    "page.rooms.title": "Salas de |estudo",
    "page.rooms.subtitle": "Crie uma sala para um livro bíblico, um tema, um grupo pequeno ou uma temporada. Qualquer um pode entrar · proprietários moderam.",
    "page.movies.kicker": "Fé na tela",
    "page.movies.title": "Filmes",
    "page.bible.kicker": "As Sagradas Escrituras",
    "page.bible.title": "Explore a Bíblia",
    "page.bible.subtitle": "Escolha um livro e um capítulo. Toque em reproduzir para ouvir no seu idioma.",
    "page.bible.tabAll": "Os 66",
    "page.bible.tabOld": "Antigo Testamento",
    "page.bible.tabNew": "Novo Testamento",
    "page.bible.filterPlaceholder": "Filtrar livros...",
    "page.kids.kicker": "Para os pequenos",
    "page.kids.title": "Crianças e desenhos",
    "page.churches.kicker": "Encontre sua igreja",
    "page.churches.title": "Localizador de igrejas",
    "page.churches.subtitle": "Toque em um botão para encontrar igrejas cristãs perto de você — todas as denominações. Dados de mapas abertos.",
    "page.gospel.kicker": "Música da alma",
    "page.gospel.title": "Música gospel",
    "page.discover.kicker": "Em alta esta semana",
    "page.discover.title": "Descobrir",
    "page.discover.subtitle": "Trechos de sermões, letras e falas que nossa comunidade está compartilhando agora. Toque para saltar para aquele segundo.",
    "auth.welcomeBack": "Bem-vindo de volta",
    "auth.signInSubtitle": "Entre para acessar seus versículos salvos.",
    "auth.email": "E-mail",
    "auth.password": "Senha",
    "auth.forgotPassword": "Esqueceu a senha?",
    "auth.signingIn": "Entrando…",
    "auth.or": "ou",
    "auth.continueWithGoogle": "Continuar com Google",
    "discover.empty.title": "Nenhum momento compartilhado esta semana",
    "discover.empty.body": "Enquanto você assiste ou ouve algo — um sermão, um short, uma live, uma música ou um vídeo — toque no botão 🔥 Pin no player para salvar o momento exato que você ama. Ele aparece aqui, e quem tocar pula direto para aquele ponto. Os momentos mais fixados da semana iluminam o ranking. O seu pode ser o próximo.",
    "discover.empty.cta": "Comece pelos sermões",
    "discover.loading": "Carregando o ranking…",
    "discover.error": "Não foi possível carregar o ranking agora. Tente de novo em um momento.",
    "voice.hintKicker": "Experimente o controle por voz",
    "voice.hintBody": "Diga \"play Casting Crowns\"",
    "voice.hintHelp": "Toque o microfone para começar →",
  },

  ru: {
    "nav.bible": "Библия",
    "nav.rooms": "Комнаты",
    "nav.prayers": "Молитвы",
    "nav.sermons": "Проповеди",
    "nav.movies": "Фильмы",
    "nav.kids": "Дети",
    "nav.churches": "Церкви",
    "nav.more": "Ещё",
    "nav.signIn": "Войти",
    "nav.signOut": "Выйти",
    "nav.goLive": "В эфире",
    "nav.home": "Главная",
    "nav.settings": "Настройки",
    "footer.brand": "Holy Words · Открытый библейский спутник",
    "footer.privacy": "Конфиденциальность",
    "footer.terms": "Условия",
    "footer.settings": "Настройки",
    "today.devotionalLabel": "Сегодняшнее размышление",
    "today.devotionalHeading": "Один стих. Одно размышление. Здесь и сейчас.",
    "today.readAloud": "Читать вслух",
    "today.pause": "Пауза",
    "today.playBriefing": "Воспроизвести сегодняшний обзор",
    "today.onThisDay": "В этот день в чёрной истории",
    "today.somethingGood": "Что-то хорошее",
    "today.someoneToKnow": "Кого-то, кого стоит знать",
    "today.previousDays": "Прошлые дни",
    "today.readMore": "Читать дальше",
    "today.handpickedTitle": "Отобрано на сегодня",
    "today.seeAll": "Смотреть всё",
    "action.play": "Воспроизвести",
    "action.pause": "Пауза",
    "action.stop": "Стоп",
    "action.share": "Поделиться",
    "action.save": "Сохранить",
    "action.cancel": "Отмена",
    "action.close": "Закрыть",
    "action.gotIt": "Понятно",
    "action.upload": "Загрузить",
    "action.post": "Опубликовать",
    "action.loading": "Загрузка…",
    "action.tryAgain": "Попробовать снова",
    "action.scrollTop": "Наверх",
    "action.scrollBottom": "Вниз",
    "verse.label": "Стих дня",
    "verse.openChapter": "Открыть главу",
    "settings.title": "Настройки",
    "settings.account": "Аккаунт",
    "settings.language": "Язык",
    "settings.notifications": "Уведомления",
    "settings.theme": "Тема",
    "settings.signOut": "Выйти",
    "settings.premium": "Премиум",
    "player.nowPlaying": "Сейчас играет",
    "player.upNext": "Далее",
    "player.skip": "Пропустить",
    "chip.home": "Главная",
    "chip.trending": "В тренде",
    "chip.animatedBible": "Анимированная Библия",
    "chip.apocrypha": "Апокрифы",
    "chip.bible": "Библия",
    "chip.biblePrecepts": "Библейские заповеди",
    "chip.blackAmericanFilms": "Афроамериканское кино",
    "chip.bookmarks": "Закладки",
    "chip.christianRap": "Христианский рэп",
    "chip.choir": "Хор",
    "chip.churches": "Церкви",
    "chip.comedy": "Комедия",
    "chip.communityShorts": "Короткое видео сообщества",
    "chip.concordance": "Конкорданс",
    "chip.congregationLive": "Собрание в прямом эфире",
    "chip.creatorStudio": "Студия автора",
    "chip.dailyDevotional": "Ежедневное размышление",
    "chip.deviceCheck": "Проверка устройства",
    "chip.dictionary": "Словарь",
    "chip.discoverShorts": "Шорты для вас",
    "chip.faithNews": "Новости веры",
    "chip.foundationalComedy": "Классическая комедия",
    "chip.friends": "Друзья",
    "chip.gospelMusic": "Госпел",
    "chip.history": "История",
    "chip.kidsCartoons": "Дети и мультфильмы",
    "chip.memorial": "Памяти",
    "chip.messages": "Сообщения",
    "chip.movies": "Фильмы",
    "chip.musicVideos": "Музыкальные клипы",
    "chip.myLineup": "Моя подборка",
    "chip.myMix": "Мой микс",
    "chip.myNotes": "Мои заметки",
    "chip.myPrayers": "Мои молитвы",
    "chip.myProfile": "Мой профиль",
    "chip.pictureBible": "Иллюстрированная Библия",
    "chip.prayerCircles": "Круги молитвы",
    "chip.prayers": "Молитвы",
    "chip.playlists": "Плейлисты",
    "chip.puzzles": "Головоломки",
    "chip.rooms": "Комнаты",
    "chip.search": "Поиск",
    "chip.sermons": "Проповеди",
    "chip.settings": "Настройки",
    "chip.studyGroup": "Группа изучения",
    "chip.subscriptions": "Подписки",
    "chip.sundayDinner": "Воскресный ужин",
    "chip.sundaySchool": "Воскресная школа",
    "chip.supportUs": "Поддержать",
    "chip.wallOfFaith": "Стена веры",
    "chip.wedLessons": "Уроки среды",
    "page.sermons.kicker": "С кафедры",
    "page.sermons.title": "Воскресные проповеди",
    "page.sermons.subtitle": "Растущая библиотека ИИ-проповедей. Слушайте в HD-качестве или подпишитесь на подкаст.",
    "page.prayers.kicker": "Горница",
    "page.prayers.title": "Стена |молитвы",
    "page.prayers.subtitle": "\"Носите бремена друг друга, и таким образом исполните закон Христов.\" — Галатам 6:2",
    "page.rooms.kicker": "Общение",
    "page.rooms.title": "Учебные |комнаты",
    "page.rooms.subtitle": "Создайте комнату для книги Библии, темы, малой группы или сезона. Все могут присоединиться · владельцы модерируют.",
    "page.movies.kicker": "Вера на экране",
    "page.movies.title": "Фильмы",
    "page.bible.kicker": "Священные Писания",
    "page.bible.title": "Открыть Библию",
    "page.bible.subtitle": "Выберите книгу и главу. Нажмите воспроизведение, чтобы услышать на вашем языке.",
    "page.bible.tabAll": "Все 66",
    "page.bible.tabOld": "Ветхий Завет",
    "page.bible.tabNew": "Новый Завет",
    "page.bible.filterPlaceholder": "Фильтр книг...",
    "page.kids.kicker": "Для малышей",
    "page.kids.title": "Дети и мультфильмы",
    "page.churches.kicker": "Найти церковь",
    "page.churches.title": "Поиск церквей",
    "page.churches.subtitle": "Одним нажатием найдите все христианские церкви рядом — всех конфессий. На основе открытых карт.",
    "page.gospel.kicker": "Соул-музыка",
    "page.gospel.title": "Госпел",
    "page.discover.kicker": "В тренде на неделе",
    "page.discover.title": "Открытия",
    "page.discover.subtitle": "Отрывки проповедей, слова песен и фильмов, которыми сейчас больше всего делятся. Нажмите карточку — попадёте на нужную секунду.",
    "auth.welcomeBack": "С возвращением",
    "auth.signInSubtitle": "Войдите, чтобы получить доступ к сохранённым стихам.",
    "auth.email": "Email",
    "auth.password": "Пароль",
    "auth.forgotPassword": "Забыли пароль?",
    "auth.signingIn": "Вход…",
    "auth.or": "или",
    "auth.continueWithGoogle": "Продолжить с Google",
    "discover.empty.title": "На этой неделе ещё никто не делился моментами",
    "discover.empty.body": "Во время просмотра или прослушивания чего угодно — проповеди, шортса, трансляции, песни или видео — нажмите кнопку 🔥 Pin в плеере, чтобы сохранить любимый момент. Он появится здесь, и каждый, кто нажмёт, перейдёт прямо к этому моменту. Самые закреплённые моменты недели зажигают рейтинг. Ваш может стать следующим.",
    "discover.empty.cta": "Начните с проповедей",
    "discover.loading": "Загружаем рейтинг…",
    "discover.error": "Сейчас не получилось загрузить рейтинг. Попробуйте через минуту.",
    "voice.hintKicker": "Попробуйте голосовое управление",
    "voice.hintBody": "Скажите \"play Casting Crowns\"",
    "voice.hintHelp": "Нажмите на микрофон, чтобы начать →",
  },

  hi: {
    "nav.bible": "बाइबल",
    "nav.rooms": "कक्ष",
    "nav.prayers": "प्रार्थनाएँ",
    "nav.sermons": "प्रवचन",
    "nav.movies": "फिल्में",
    "nav.kids": "बच्चे",
    "nav.churches": "गिरजे",
    "nav.more": "और",
    "nav.signIn": "साइन इन",
    "nav.signOut": "बाहर",
    "nav.goLive": "लाइव जाएँ",
    "nav.home": "होम",
    "nav.settings": "सेटिंग्स",
    "footer.brand": "Holy Words · खुला बाइबल साथी",
    "footer.privacy": "गोपनीयता",
    "footer.terms": "शर्तें",
    "footer.settings": "सेटिंग्स",
    "today.devotionalLabel": "आज का भक्ति-पाठ",
    "today.devotionalHeading": "एक पद। एक चिंतन। जहाँ आप हैं वहीं।",
    "today.readAloud": "ज़ोर से पढ़ें",
    "today.pause": "रोकें",
    "today.playBriefing": "आज की संक्षिप्ति चलाएँ",
    "today.onThisDay": "ब्लैक इतिहास में आज का दिन",
    "today.somethingGood": "कुछ अच्छा",
    "today.someoneToKnow": "किसे जानना चाहिए",
    "today.previousDays": "पिछले दिन",
    "today.readMore": "और पढ़ें",
    "today.handpickedTitle": "आज रात के लिए चुने हुए",
    "today.seeAll": "सब देखें",
    "action.play": "चलाएँ",
    "action.pause": "रोकें",
    "action.stop": "बंद करें",
    "action.share": "साझा करें",
    "action.save": "सहेजें",
    "action.cancel": "रद्द करें",
    "action.close": "बंद करें",
    "action.gotIt": "समझ गया",
    "action.upload": "अपलोड करें",
    "action.post": "पोस्ट करें",
    "action.loading": "लोड हो रहा है…",
    "action.tryAgain": "फिर कोशिश करें",
    "action.scrollTop": "ऊपर जाएँ",
    "action.scrollBottom": "नीचे जाएँ",
    "verse.label": "आज का पद",
    "verse.openChapter": "अध्याय खोलें",
    "settings.title": "सेटिंग्स",
    "settings.account": "खाता",
    "settings.language": "भाषा",
    "settings.notifications": "सूचनाएँ",
    "settings.theme": "थीम",
    "settings.signOut": "साइन आउट",
    "settings.premium": "प्रीमियम",
    "player.nowPlaying": "अभी चल रहा है",
    "player.upNext": "अगला",
    "player.skip": "छोड़ें",
    "chip.home": "होम",
    "chip.trending": "ट्रेंडिंग",
    "chip.animatedBible": "एनिमेटेड बाइबल",
    "chip.apocrypha": "अपोक्रिफा",
    "chip.bible": "बाइबल",
    "chip.biblePrecepts": "बाइबल नियम",
    "chip.blackAmericanFilms": "ब्लैक अमेरिकन फिल्में",
    "chip.bookmarks": "बुकमार्क",
    "chip.christianRap": "क्रिश्चियन रैप",
    "chip.choir": "गायक मंडली",
    "chip.churches": "गिरजे",
    "chip.comedy": "कॉमेडी",
    "chip.communityShorts": "सामुदायिक शॉर्ट्स",
    "chip.concordance": "कन्कॉर्डेंस",
    "chip.congregationLive": "समूह लाइव",
    "chip.creatorStudio": "क्रिएटर स्टूडियो",
    "chip.dailyDevotional": "दैनिक भक्ति",
    "chip.deviceCheck": "डिवाइस जाँच",
    "chip.dictionary": "शब्दकोश",
    "chip.discoverShorts": "शॉर्ट्स खोजें",
    "chip.faithNews": "विश्वास समाचार",
    "chip.foundationalComedy": "मूल कॉमेडी",
    "chip.friends": "मित्र",
    "chip.gospelMusic": "गॉस्पेल संगीत",
    "chip.history": "इतिहास",
    "chip.kidsCartoons": "बच्चे और कार्टून",
    "chip.memorial": "स्मारक",
    "chip.messages": "संदेश",
    "chip.movies": "फिल्में",
    "chip.musicVideos": "संगीत वीडियो",
    "chip.myLineup": "मेरी सूची",
    "chip.myMix": "मेरा मिक्स",
    "chip.myNotes": "मेरे नोट्स",
    "chip.myPrayers": "मेरी प्रार्थनाएँ",
    "chip.myProfile": "मेरी प्रोफ़ाइल",
    "chip.pictureBible": "चित्र बाइबल",
    "chip.prayerCircles": "प्रार्थना मंडल",
    "chip.prayers": "प्रार्थनाएँ",
    "chip.playlists": "प्लेलिस्ट",
    "chip.puzzles": "पहेलियाँ",
    "chip.rooms": "कक्ष",
    "chip.search": "खोज",
    "chip.sermons": "प्रवचन",
    "chip.settings": "सेटिंग्स",
    "chip.studyGroup": "अध्ययन समूह",
    "chip.subscriptions": "सब्सक्रिप्शन",
    "chip.sundayDinner": "रविवार का खाना",
    "chip.sundaySchool": "रविवार पाठशाला",
    "chip.supportUs": "समर्थन",
    "chip.wallOfFaith": "विश्वास की दीवार",
    "chip.wedLessons": "बुधवार पाठ",
    "page.sermons.kicker": "मंच से",
    "page.sermons.title": "रविवार के प्रवचन",
    "page.sermons.subtitle": "AI द्वारा तैयार प्रवचनों की बढ़ती लाइब्रेरी। स्टूडियो HD आवाज़ में सुनें या पॉडकास्ट के रूप में सब्सक्राइब करें।",
    "page.prayers.kicker": "ऊपरी कक्ष",
    "page.prayers.title": "प्रार्थना |दीवार",
    "page.prayers.subtitle": "\"एक दूसरे का बोझ उठाओ और इस प्रकार मसीह की व्यवस्था को पूरा करो।\" — गलातियों 6:2",
    "page.rooms.kicker": "संगति",
    "page.rooms.title": "अध्ययन |कक्ष",
    "page.rooms.subtitle": "किसी बाइबल पुस्तक, विषय, छोटे समूह या सीज़न के लिए कक्ष बनाएँ। कोई भी जुड़ सकता है · मालिक मॉडरेट करते हैं।",
    "page.movies.kicker": "स्क्रीन पर विश्वास",
    "page.movies.title": "फिल्में",
    "page.bible.kicker": "पवित्र शास्त्र",
    "page.bible.title": "बाइबल देखें",
    "page.bible.subtitle": "एक पुस्तक और अध्याय चुनें। अपनी भाषा में सुनने के लिए प्ले बटन दबाएँ।",
    "page.bible.tabAll": "सभी 66",
    "page.bible.tabOld": "पुराना नियम",
    "page.bible.tabNew": "नया नियम",
    "page.bible.filterPlaceholder": "पुस्तकें फ़िल्टर करें...",
    "page.kids.kicker": "नन्हे-मुन्नों के लिए",
    "page.kids.title": "बच्चे और कार्टून",
    "page.churches.kicker": "अपना गिरजा खोजें",
    "page.churches.title": "गिरजा खोजी",
    "page.churches.subtitle": "एक टैप से अपने पास की हर ईसाई गिरजा खोजें — सभी सम्प्रदाय। खुले मानचित्र डेटा से चालित।",
    "page.gospel.kicker": "आत्मा का संगीत",
    "page.gospel.title": "गॉस्पेल संगीत",
    "page.discover.kicker": "इस हफ़्ते ट्रेंडिंग",
    "page.discover.title": "खोजें",
    "page.discover.subtitle": "अभी सबसे ज़्यादा शेयर किए जा रहे प्रवचन क्लिप, गीत और फ़िल्म डायलॉग। किसी कार्ड पर टैप करें और सीधे उस पल पर जाएँ।",
    "auth.welcomeBack": "वापसी पर स्वागत है",
    "auth.signInSubtitle": "अपने सहेजे गए पद देखने के लिए साइन इन करें।",
    "auth.email": "ईमेल",
    "auth.password": "पासवर्ड",
    "auth.forgotPassword": "पासवर्ड भूल गए?",
    "auth.signingIn": "साइन इन हो रहा है…",
    "auth.or": "या",
    "auth.continueWithGoogle": "Google के साथ जारी रखें",
    "discover.empty.title": "इस हफ़्ते अभी तक कोई पल साझा नहीं हुआ",
    "discover.empty.body": "जब आप कुछ भी देख या सुन रहे हों — कोई प्रवचन, शॉर्ट, लाइव, गीत या वीडियो — तो प्लेयर में 🔥 Pin बटन दबाकर अपना पसंदीदा सटीक पल सहेजें। यह यहाँ दिखेगा, और जो भी उसे छूएगा सीधे उसी पल पर पहुँच जाएगा। इस सप्ताह सबसे ज़्यादा पिन किए गए पल बोर्ड को रोशन करते हैं। अगला आपका हो सकता है।",
    "discover.empty.cta": "प्रवचनों से शुरू करें",
    "discover.loading": "लीडरबोर्ड लोड हो रहा है…",
    "discover.error": "अभी लीडरबोर्ड लोड नहीं हो सका। एक पल में फिर कोशिश करें।",
    "voice.hintKicker": "वॉइस कंट्रोल आज़माएँ",
    "voice.hintBody": "कहें \"play Casting Crowns\"",
    "voice.hintHelp": "शुरू करने के लिए माइक दबाएँ →",
  },

  ar: {
    "nav.bible": "الكتاب المقدس",
    "nav.rooms": "الغرف",
    "nav.prayers": "الصلوات",
    "nav.sermons": "العظات",
    "nav.movies": "الأفلام",
    "nav.kids": "الأطفال",
    "nav.churches": "الكنائس",
    "nav.more": "المزيد",
    "nav.signIn": "تسجيل الدخول",
    "nav.signOut": "خروج",
    "nav.goLive": "بث مباشر",
    "nav.home": "الرئيسية",
    "nav.settings": "الإعدادات",
    "footer.brand": "Holy Words · رفيقك المفتوح للكتاب المقدس",
    "footer.privacy": "الخصوصية",
    "footer.terms": "الشروط",
    "footer.settings": "الإعدادات",
    "today.devotionalLabel": "تأمل اليوم",
    "today.devotionalHeading": "آية واحدة. تأمل واحد. حيث أنت بالضبط.",
    "today.readAloud": "اقرأ بصوت عالٍ",
    "today.pause": "إيقاف مؤقت",
    "today.playBriefing": "تشغيل ملخص اليوم",
    "today.onThisDay": "في مثل هذا اليوم في التاريخ الأسود",
    "today.somethingGood": "شيء جميل",
    "today.someoneToKnow": "شخص يجب أن تعرفه",
    "today.previousDays": "الأيام السابقة",
    "today.readMore": "اقرأ المزيد",
    "today.handpickedTitle": "مختارات لهذه الليلة",
    "today.seeAll": "عرض الكل",
    "action.play": "تشغيل",
    "action.pause": "إيقاف مؤقت",
    "action.stop": "إيقاف",
    "action.share": "مشاركة",
    "action.save": "حفظ",
    "action.cancel": "إلغاء",
    "action.close": "إغلاق",
    "action.gotIt": "فهمت",
    "action.upload": "رفع",
    "action.post": "نشر",
    "action.loading": "جارٍ التحميل…",
    "action.tryAgain": "حاول مرة أخرى",
    "action.scrollTop": "إلى الأعلى",
    "action.scrollBottom": "إلى الأسفل",
    "verse.label": "آية اليوم",
    "verse.openChapter": "فتح الفصل",
    "settings.title": "الإعدادات",
    "settings.account": "الحساب",
    "settings.language": "اللغة",
    "settings.notifications": "الإشعارات",
    "settings.theme": "السمة",
    "settings.signOut": "تسجيل الخروج",
    "settings.premium": "بريميوم",
    "player.nowPlaying": "يُشغَّل الآن",
    "player.upNext": "التالي",
    "player.skip": "تخطّ",
    "chip.home": "الرئيسية",
    "chip.trending": "الرائج",
    "chip.animatedBible": "الكتاب المقدس المتحرك",
    "chip.apocrypha": "الأبوكريفا",
    "chip.bible": "الكتاب المقدس",
    "chip.biblePrecepts": "وصايا الكتاب",
    "chip.blackAmericanFilms": "أفلام أمريكية سوداء",
    "chip.bookmarks": "المفضلة",
    "chip.christianRap": "راب مسيحي",
    "chip.choir": "الجوقة",
    "chip.churches": "الكنائس",
    "chip.comedy": "كوميديا",
    "chip.communityShorts": "فيديوهات المجتمع",
    "chip.concordance": "الفهرس الكتابي",
    "chip.congregationLive": "الكنيسة مباشر",
    "chip.creatorStudio": "استوديو المنشئ",
    "chip.dailyDevotional": "تأمل يومي",
    "chip.deviceCheck": "فحص الجهاز",
    "chip.dictionary": "القاموس",
    "chip.discoverShorts": "اكتشف الفيديوهات",
    "chip.faithNews": "أخبار الإيمان",
    "chip.foundationalComedy": "الكوميديا الأساسية",
    "chip.friends": "الأصدقاء",
    "chip.gospelMusic": "موسيقى الإنجيل",
    "chip.history": "التاريخ",
    "chip.kidsCartoons": "أطفال ورسوم",
    "chip.memorial": "النصب التذكاري",
    "chip.messages": "الرسائل",
    "chip.movies": "الأفلام",
    "chip.musicVideos": "كليبات",
    "chip.myLineup": "قائمتي",
    "chip.myMix": "خلطتي",
    "chip.myNotes": "ملاحظاتي",
    "chip.myPrayers": "صلواتي",
    "chip.myProfile": "حسابي",
    "chip.pictureBible": "كتاب مقدس مصور",
    "chip.prayerCircles": "حلقات الصلاة",
    "chip.prayers": "الصلوات",
    "chip.playlists": "قوائم التشغيل",
    "chip.puzzles": "ألغاز",
    "chip.rooms": "الغرف",
    "chip.search": "بحث",
    "chip.sermons": "العظات",
    "chip.settings": "الإعدادات",
    "chip.studyGroup": "مجموعة الدراسة",
    "chip.subscriptions": "الاشتراكات",
    "chip.sundayDinner": "عشاء الأحد",
    "chip.sundaySchool": "مدرسة الأحد",
    "chip.supportUs": "ادعمنا",
    "chip.wallOfFaith": "جدار الإيمان",
    "chip.wedLessons": "دروس الأربعاء",
    "page.sermons.kicker": "من المنبر",
    "page.sermons.title": "عظات الأحد",
    "page.sermons.subtitle": "مكتبة متنامية من العظات المُعَدّة بالذكاء الاصطناعي. استمع بصوت HD أو اشترك كبودكاست.",
    "page.prayers.kicker": "العلية",
    "page.prayers.title": "جدار |الصلاة",
    "page.prayers.subtitle": "\"احملوا بعضكم أثقال بعض، وهكذا تتمّمون ناموس المسيح.\" — غلاطية 6:2",
    "page.rooms.kicker": "الشركة",
    "page.rooms.title": "غرف |الدراسة",
    "page.rooms.subtitle": "أنشئ غرفة لسفر من الكتاب، أو موضوع، أو مجموعة صغيرة، أو موسم. يمكن للجميع الانضمام · المالكون يديرون.",
    "page.movies.kicker": "الإيمان على الشاشة",
    "page.movies.title": "الأفلام",
    "page.bible.kicker": "الكتب المقدسة",
    "page.bible.title": "تصفّح الكتاب المقدس",
    "page.bible.subtitle": "اختر سفرًا وأصحاحًا. اضغط على زر التشغيل لسماعه بلغتك.",
    "page.bible.tabAll": "الـ66",
    "page.bible.tabOld": "العهد القديم",
    "page.bible.tabNew": "العهد الجديد",
    "page.bible.filterPlaceholder": "تصفية الأسفار...",
    "page.kids.kicker": "للصغار",
    "page.kids.title": "أطفال ورسوم",
    "page.churches.kicker": "ابحث عن كنيستك",
    "page.churches.title": "دليل الكنائس",
    "page.churches.subtitle": "اضغط زرًا واحدًا للعثور على كل الكنائس المسيحية القريبة — جميع المذاهب. بيانات خرائط مجتمعية مفتوحة.",
    "page.gospel.kicker": "موسيقى الروح",
    "page.gospel.title": "موسيقى الإنجيل",
    "page.discover.kicker": "الرائج هذا الأسبوع",
    "page.discover.title": "اكتشف",
    "page.discover.subtitle": "مقاطع العظات وكلمات الأغاني ومشاهد الأفلام التي يشاركها مجتمعنا الآن. انقر بطاقة لتقفز إلى تلك الثانية.",
    "auth.welcomeBack": "مرحبًا بعودتك",
    "auth.signInSubtitle": "سجّل دخولك للوصول إلى آياتك المحفوظة.",
    "auth.email": "البريد الإلكتروني",
    "auth.password": "كلمة المرور",
    "auth.forgotPassword": "هل نسيت كلمة المرور؟",
    "auth.signingIn": "جارٍ تسجيل الدخول…",
    "auth.or": "أو",
    "auth.continueWithGoogle": "المتابعة مع Google",
    "discover.empty.title": "لم تتم مشاركة أي لحظة هذا الأسبوع بعد",
    "discover.empty.body": "أثناء مشاهدتك أو استماعك لأي شيء — عظة أو مقطع قصير أو بث مباشر أو أنشودة أو فيديو — اضغط زر 🔥 Pin في المشغّل لتحفظ اللحظة التي أحببتها بالضبط. ستظهر هنا، ومن يضغط عليها ينتقل مباشرة إلى تلك اللحظة. أكثر اللحظات تثبيتًا هذا الأسبوع تُضيء اللوحة. قد تكون لحظتك هي التالية.",
    "discover.empty.cta": "ابدأ بالعظات",
    "discover.loading": "جارٍ تحميل لوحة الصدارة…",
    "discover.error": "تعذّر تحميل لوحة الصدارة الآن. حاول مرة أخرى بعد لحظة.",
    "voice.hintKicker": "جرّب التحكم بالصوت",
    "voice.hintBody": "قل \"play Casting Crowns\"",
    "voice.hintHelp": "اضغط الميكروفون للبدء ←",
  },

  "zh-CN": {
    "nav.bible": "圣经",
    "nav.rooms": "房间",
    "nav.prayers": "祷告",
    "nav.sermons": "讲道",
    "nav.movies": "电影",
    "nav.kids": "儿童",
    "nav.churches": "教会",
    "nav.more": "更多",
    "nav.signIn": "登录",
    "nav.signOut": "退出",
    "nav.goLive": "直播",
    "nav.home": "首页",
    "nav.settings": "设置",
    "footer.brand": "Holy Words · 您的开放圣经伴侣",
    "footer.privacy": "隐私",
    "footer.terms": "条款",
    "footer.settings": "设置",
    "today.devotionalLabel": "今日灵修",
    "today.devotionalHeading": "一节经文。一段反思。就在你所在之处。",
    "today.readAloud": "朗读",
    "today.pause": "暂停",
    "today.playBriefing": "播放今日简报",
    "today.onThisDay": "黑人历史上的今天",
    "today.somethingGood": "美好的事",
    "today.someoneToKnow": "你应该认识的人",
    "today.previousDays": "往日",
    "today.readMore": "阅读更多",
    "today.handpickedTitle": "今晚精选",
    "today.seeAll": "查看全部",
    "action.play": "播放",
    "action.pause": "暂停",
    "action.stop": "停止",
    "action.share": "分享",
    "action.save": "保存",
    "action.cancel": "取消",
    "action.close": "关闭",
    "action.gotIt": "知道了",
    "action.upload": "上传",
    "action.post": "发布",
    "action.loading": "加载中…",
    "action.tryAgain": "再试一次",
    "action.scrollTop": "回到顶部",
    "action.scrollBottom": "回到底部",
    "verse.label": "今日经文",
    "verse.openChapter": "打开章节",
    "settings.title": "设置",
    "settings.account": "账户",
    "settings.language": "语言",
    "settings.notifications": "通知",
    "settings.theme": "主题",
    "settings.signOut": "登出",
    "settings.premium": "高级版",
    "player.nowPlaying": "正在播放",
    "player.upNext": "接下来",
    "player.skip": "跳过",
    "chip.home": "首页",
    "chip.trending": "热门",
    "chip.animatedBible": "动画圣经",
    "chip.apocrypha": "次经",
    "chip.bible": "圣经",
    "chip.biblePrecepts": "圣经教导",
    "chip.blackAmericanFilms": "黑人电影",
    "chip.bookmarks": "书签",
    "chip.christianRap": "基督说唱",
    "chip.choir": "唱诗班",
    "chip.churches": "教会",
    "chip.comedy": "喜剧",
    "chip.communityShorts": "社区短视频",
    "chip.concordance": "经文索引",
    "chip.congregationLive": "教会直播",
    "chip.creatorStudio": "创作者工作室",
    "chip.dailyDevotional": "每日灵修",
    "chip.deviceCheck": "设备检查",
    "chip.dictionary": "词典",
    "chip.discoverShorts": "发现短视频",
    "chip.faithNews": "信仰新闻",
    "chip.foundationalComedy": "经典喜剧",
    "chip.friends": "好友",
    "chip.gospelMusic": "福音音乐",
    "chip.history": "历史",
    "chip.kidsCartoons": "儿童与动画",
    "chip.memorial": "纪念",
    "chip.messages": "消息",
    "chip.movies": "电影",
    "chip.musicVideos": "音乐视频",
    "chip.myLineup": "我的节目",
    "chip.myMix": "我的合辑",
    "chip.myNotes": "我的笔记",
    "chip.myPrayers": "我的祷告",
    "chip.myProfile": "我的资料",
    "chip.pictureBible": "图画圣经",
    "chip.prayerCircles": "祷告圈",
    "chip.prayers": "祷告",
    "chip.playlists": "播放列表",
    "chip.puzzles": "拼图",
    "chip.rooms": "房间",
    "chip.search": "搜索",
    "chip.sermons": "讲道",
    "chip.settings": "设置",
    "chip.studyGroup": "查经小组",
    "chip.subscriptions": "订阅",
    "chip.sundayDinner": "周日晚餐",
    "chip.sundaySchool": "主日学",
    "chip.supportUs": "支持我们",
    "chip.wallOfFaith": "信心墙",
    "chip.wedLessons": "周三课程",
    "page.sermons.kicker": "来自讲台",
    "page.sermons.title": "主日讲道",
    "page.sermons.subtitle": "由 AI 准备的不断增长的讲道库。以工作室级 HD 语音收听或订阅播客。",
    "page.prayers.kicker": "马可楼",
    "page.prayers.title": "祷告|墙",
    "page.prayers.subtitle": "\"你们各人的重担要互相担当,如此就完全了基督的律法。\" — 加拉太书 6:2",
    "page.rooms.kicker": "团契",
    "page.rooms.title": "查经|房间",
    "page.rooms.subtitle": "为一本圣经书、一个主题、一个小组或一个季节创建房间。任何人都可加入 · 房主管理。",
    "page.movies.kicker": "屏幕上的信仰",
    "page.movies.title": "电影",
    "page.bible.kicker": "圣经",
    "page.bible.title": "浏览圣经",
    "page.bible.subtitle": "选择一本书和一章。点播放按钮,用您的语言朗读。",
    "page.bible.tabAll": "全部 66",
    "page.bible.tabOld": "旧约",
    "page.bible.tabNew": "新约",
    "page.bible.filterPlaceholder": "筛选书卷...",
    "page.kids.kicker": "为小朋友",
    "page.kids.title": "儿童与动画",
    "page.churches.kicker": "寻找属灵之家",
    "page.churches.title": "教会查找",
    "page.churches.subtitle": "一键找到附近所有基督教会——涵盖所有宗派。基于开放社区地图数据。",
    "page.gospel.kicker": "灵魂音乐",
    "page.gospel.title": "福音音乐",
    "page.discover.kicker": "本周热门",
    "page.discover.title": "发现",
    "page.discover.subtitle": "社区此刻分享最多的讲道片段、歌词和电影台词。点击任意卡片直接跳转到那一秒。",
    "auth.welcomeBack": "欢迎回来",
    "auth.signInSubtitle": "登录以访问您保存的经文。",
    "auth.email": "邮箱",
    "auth.password": "密码",
    "auth.forgotPassword": "忘记密码?",
    "auth.signingIn": "登录中…",
    "auth.or": "或",
    "auth.continueWithGoogle": "使用 Google 继续",
    "discover.empty.title": "本周还没有分享的精彩瞬间",
    "discover.empty.body": "在观看或收听任何内容时——讲道、短片、直播、歌曲或视频——点击播放器中的 🔥 Pin 按钮，保存你喜欢的那个精确瞬间。它会出现在这里，任何人点击都会直接跳到那一刻。本周被收藏最多的瞬间会点亮榜单。下一个也许就是你的。",
    "discover.empty.cta": "从讲道开始",
    "discover.loading": "正在加载榜单…",
    "discover.error": "现在无法加载榜单。请稍后再试。",
    "voice.hintKicker": "试试语音控制",
    "voice.hintBody": "说 \"play Casting Crowns\"",
    "voice.hintHelp": "点击麦克风开始 →",
  },

  sw: {
    "nav.bible": "Biblia",
    "nav.rooms": "Vyumba",
    "nav.prayers": "Maombi",
    "nav.sermons": "Mahubiri",
    "nav.movies": "Filamu",
    "nav.kids": "Watoto",
    "nav.churches": "Makanisa",
    "nav.more": "Zaidi",
    "nav.signIn": "Ingia",
    "nav.signOut": "Toka",
    "nav.goLive": "Onyesha mubashara",
    "nav.home": "Nyumbani",
    "nav.settings": "Mipangilio",
    "footer.brand": "Holy Words · Mwenzako wa Biblia wazi",
    "footer.privacy": "Faragha",
    "footer.terms": "Masharti",
    "footer.settings": "Mipangilio",
    "today.devotionalLabel": "Ibada ya leo",
    "today.devotionalHeading": "Mstari mmoja. Tafakari moja. Pale ulipo.",
    "today.readAloud": "Soma kwa sauti",
    "today.pause": "Sitisha",
    "today.playBriefing": "Cheza muhtasari wa leo",
    "today.onThisDay": "Siku kama hii katika historia ya Waafrika",
    "today.somethingGood": "Jambo jema",
    "today.someoneToKnow": "Mtu wa kumjua",
    "today.previousDays": "Siku zilizopita",
    "today.readMore": "Soma zaidi",
    "today.handpickedTitle": "Chaguzi za usiku huu",
    "today.seeAll": "Tazama vyote",
    "action.play": "Cheza",
    "action.pause": "Sitisha",
    "action.stop": "Simamisha",
    "action.share": "Shiriki",
    "action.save": "Hifadhi",
    "action.cancel": "Ghairi",
    "action.close": "Funga",
    "action.gotIt": "Nimeelewa",
    "action.upload": "Pakia",
    "action.post": "Chapisha",
    "action.loading": "Inapakia…",
    "action.tryAgain": "Jaribu tena",
    "action.scrollTop": "Nenda juu",
    "action.scrollBottom": "Nenda chini",
    "verse.label": "Mstari wa leo",
    "verse.openChapter": "Fungua sura",
    "settings.title": "Mipangilio",
    "settings.account": "Akaunti",
    "settings.language": "Lugha",
    "settings.notifications": "Arifa",
    "settings.theme": "Mandhari",
    "settings.signOut": "Toka",
    "settings.premium": "Premium",
    "player.nowPlaying": "Inacheza sasa",
    "player.upNext": "Inayofuata",
    "player.skip": "Ruka",
    "chip.home": "Nyumbani",
    "chip.trending": "Yanayopendwa",
    "chip.animatedBible": "Biblia ya picha",
    "chip.apocrypha": "Apokrifa",
    "chip.bible": "Biblia",
    "chip.biblePrecepts": "Maagizo ya Biblia",
    "chip.blackAmericanFilms": "Filamu za Waafrika",
    "chip.bookmarks": "Vipendwa",
    "chip.christianRap": "Rap ya Kikristo",
    "chip.choir": "Kwaya",
    "chip.churches": "Makanisa",
    "chip.comedy": "Vichekesho",
    "chip.communityShorts": "Vifupi vya jamii",
    "chip.concordance": "Faharasa",
    "chip.congregationLive": "Kanisa mubashara",
    "chip.creatorStudio": "Studio ya muumbaji",
    "chip.dailyDevotional": "Ibada ya kila siku",
    "chip.deviceCheck": "Hakiki kifaa",
    "chip.dictionary": "Kamusi",
    "chip.discoverShorts": "Gundua vifupi",
    "chip.faithNews": "Habari za imani",
    "chip.foundationalComedy": "Vichekesho vya msingi",
    "chip.friends": "Marafiki",
    "chip.gospelMusic": "Muziki wa injili",
    "chip.history": "Historia",
    "chip.kidsCartoons": "Watoto na katuni",
    "chip.memorial": "Ukumbusho",
    "chip.messages": "Ujumbe",
    "chip.movies": "Filamu",
    "chip.musicVideos": "Video za muziki",
    "chip.myLineup": "Ratiba yangu",
    "chip.myMix": "Mchanganyiko wangu",
    "chip.myNotes": "Maelezo yangu",
    "chip.myPrayers": "Maombi yangu",
    "chip.myProfile": "Wasifu wangu",
    "chip.pictureBible": "Biblia ya picha",
    "chip.prayerCircles": "Vikundi vya maombi",
    "chip.prayers": "Maombi",
    "chip.playlists": "Orodha za kucheza",
    "chip.puzzles": "Mafumbo",
    "chip.rooms": "Vyumba",
    "chip.search": "Tafuta",
    "chip.sermons": "Mahubiri",
    "chip.settings": "Mipangilio",
    "chip.studyGroup": "Kikundi cha masomo",
    "chip.subscriptions": "Michango",
    "chip.sundayDinner": "Chakula cha Jumapili",
    "chip.sundaySchool": "Shule ya Jumapili",
    "chip.supportUs": "Tunge mkono",
    "chip.wallOfFaith": "Ukuta wa imani",
    "chip.wedLessons": "Masomo ya Jumatano",
    "page.sermons.kicker": "Kutoka mimbari",
    "page.sermons.title": "Mahubiri ya Jumapili",
    "page.sermons.subtitle": "Maktaba inayokua ya mahubiri yaliyoandaliwa na AI. Sikiliza kwa sauti ya HD au jiandikishe kama podcast.",
    "page.prayers.kicker": "Chumba cha Juu",
    "page.prayers.title": "Ukuta wa |Maombi",
    "page.prayers.subtitle": "\"Mchukuliane mizigo, na hivyo mtaitimiliza sheria ya Kristo.\" — Wagalatia 6:2",
    "page.rooms.kicker": "Ushirika",
    "page.rooms.title": "Vyumba vya |Masomo",
    "page.rooms.subtitle": "Anzisha chumba kwa kitabu cha Biblia, mada, kikundi kidogo au msimu. Yeyote anaweza kujiunga · wamiliki husimamia.",
    "page.movies.kicker": "Imani kwenye skrini",
    "page.movies.title": "Filamu",
    "page.bible.kicker": "Maandiko Matakatifu",
    "page.bible.title": "Vinjari Biblia",
    "page.bible.subtitle": "Chagua kitabu na sura. Gusa kicheza ili kusikia kwa lugha yako.",
    "page.bible.tabAll": "Zote 66",
    "page.bible.tabOld": "Agano la Kale",
    "page.bible.tabNew": "Agano Jipya",
    "page.bible.filterPlaceholder": "Chuja vitabu...",
    "page.kids.kicker": "Kwa wadogo",
    "page.kids.title": "Watoto na katuni",
    "page.churches.kicker": "Tafuta kanisa la nyumbani",
    "page.churches.title": "Mtafutaji wa makanisa",
    "page.churches.subtitle": "Bonyeza kitufe kimoja kupata makanisa ya Kikristo karibu nawe — madhehebu yote. Inatumia data huria ya ramani.",
    "page.gospel.kicker": "Muziki wa nafsi",
    "page.gospel.title": "Muziki wa injili",
    "page.discover.kicker": "Inayopendwa wiki hii",
    "page.discover.title": "Gundua",
    "page.discover.subtitle": "Vipande vya mahubiri, nyimbo na mistari ya filamu jamii yetu inashiriki zaidi sasa. Bonyeza kadi ili kuruka hadi sekunde hiyo.",
    "auth.welcomeBack": "Karibu tena",
    "auth.signInSubtitle": "Ingia kufikia mistari uliyohifadhi.",
    "auth.email": "Barua pepe",
    "auth.password": "Nenosiri",
    "auth.forgotPassword": "Umesahau nenosiri?",
    "auth.signingIn": "Inaingia…",
    "auth.or": "au",
    "auth.continueWithGoogle": "Endelea na Google",
    "discover.empty.title": "Hakuna nyakati zilizoshirikiwa wiki hii bado",
    "discover.empty.body": "Unapotazama au kusikiliza chochote — mahubiri, short, live, wimbo au video — gusa kitufe cha 🔥 Pin kwenye kichezaji ili kuhifadhi wakati hasa unaoupenda. Utaonekana hapa, na yeyote anayegusa ataruka moja kwa moja hadi mahali hapo. Nyakati zilizobandikwa zaidi wiki hii zinawasha ubao. Yako inaweza kuwa inayofuata.",
    "discover.empty.cta": "Anza na mahubiri",
    "discover.loading": "Inapakia ubao wa viongozi…",
    "discover.error": "Tumeshindwa kupakia ubao wa viongozi sasa. Jaribu tena baada ya muda mfupi.",
    "voice.hintKicker": "Jaribu udhibiti wa sauti",
    "voice.hintBody": "Sema \"play Casting Crowns\"",
    "voice.hintHelp": "Bonyeza maikrofoni kuanza →",
  },
};

export default translations;
